import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { HubRuntime } from '@geekcommerce/hub-runtime';
import { GcAparato } from '@geekcommerce/printer';

// ─── Caja central embebida ────────────────────────────────────────────────────
//
// Convierte ESTA tablet en la caja central: arranca geekcommerce-hub con Node
// embebido (nodejs-mobile) dentro de la app y las demás tablets se conectan a
// la IP del WiFi de esta. Dos capas nativas según plataforma:
//   iOS     → plugin propio HubRuntime (plugins/hub-runtime)
//   Android → capacitor-nodejs
// En ambos casos la app habla con el hub por HTTP en 127.0.0.1, igual que
// cualquier otra tablet; no hay un canal especial.

export const LOCAL_HUB_URL = 'http://127.0.0.1:4747';

const platform = () => Capacitor.getPlatform();

export function embeddedHubAvailable() {
  if (!Capacitor.isNativePlatform()) return false;
  if (platform() === 'ios')     return Capacitor.isPluginAvailable('HubRuntime');
  if (platform() === 'android') return Capacitor.isPluginAvailable('CapacitorNodeJS');
  return false;
}

// fetch con límite: sin él, una petición colgada dejaba "Ser la caja" o el
// arranque esperando para siempre.
async function estadoCaja(url, ms = 2500) {
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), ms);
  try {
    const res = await fetch(`${url}/hub/status`, { cache: 'no-store', signal: ctrl.signal });
    const json = await res.json();
    return json?.data?.hub ? json.data : null;
  } finally { clearTimeout(t); }
}

// La caja principal no apaga la pantalla (Android duerme la red y congela la
// app con la pantalla apagada, y las comanderas se quedan sin caja).
export function mantenerPantalla(encendida) {
  if (Capacitor.getPlatform() !== 'android') return;   // iOS: lo hace su plugin
  GcAparato.mantenerEncendida({ encendida }).catch(() => {});
}

async function waitForHub(url, timeoutMs = 25000, falloArranque = () => null) {
  const deadline = Date.now() + timeoutMs;
  let lastErr = null;
  while (Date.now() < deadline) {
    const fallo = falloArranque();
    if (fallo) throw fallo;
    try {
      const st = await estadoCaja(url);
      if (st) return st;
    } catch (err) { lastErr = err; }
    await new Promise((r) => setTimeout(r, 500));
  }
  throw new Error(`La caja no respondió en ${Math.round(timeoutMs / 1000)} s${lastErr ? ` (${lastErr.message})` : ''}`);
}

// Arranca (o confirma que ya corre) el hub embebido y espera a que conteste.
// Devuelve el /hub/status, que trae las URLs de red local para las otras tablets.
export async function startEmbeddedHub({ name = 'Caja (tablet)', cloudUrl } = {}) {
  if (!embeddedHubAvailable()) throw new Error('Esta plataforma no puede ser caja central');

  const env = {
    HUB_NAME:  name,
    HUB_PORT:  '4747',
    CLOUD_URL: cloudUrl ?? import.meta.env.VITE_API_URL ?? '',
  };

  if (platform() === 'ios') {
    await HubRuntime.start({ env });
  } else {
    // capacitor-nodejs no sabe de carpetas de la app: la escribible se calcula aquí
    const { uri } = await Filesystem.getUri({ directory: Directory.Data, path: 'gc-hub' });
    env.HUB_DATA_DIR = decodeURIComponent(uri.replace(/^file:\/\//, ''));
    // Si ya corre (la pantalla se volvió a crear, o se reabrió la app sin que
    // el proceso muriera) no se arranca otra vez: Node solo puede arrancar una
    // vez por proceso y un segundo intento tumba la app.
    const yaCorre = await estadoCaja(LOCAL_HUB_URL, 1500).catch(() => null);
    if (yaCorre) { mantenerPantalla(true); return yaCorre; }
    const { NodeJS } = await import('capacitor-nodejs');
    // SIN await: en capacitor-nodejs la promesa de start() se resuelve cuando
    // Node TERMINA, o sea nunca mientras la caja trabaja. Con await, "Ser la
    // caja" se quedaba cargando para siempre: la tablet nunca mostraba su
    // dirección ni quedaba guardada como caja, y al reabrir la app ya no había
    // caja. Se arranca, y se sabe que está lista cuando contesta por HTTP.
    let fallo = null;
    NodeJS.start({ env }).catch((err) => {
      if (!/already/i.test(err?.message ?? '')) fallo = err;
    });
    const st = await waitForHub(LOCAL_HUB_URL, 25000, () => fallo);
    mantenerPantalla(true);
    return st;
  }

  return waitForHub(LOCAL_HUB_URL);
}

export async function embeddedHubRunning() {
  if (!embeddedHubAvailable()) return false;
  try {
    if (platform() === 'ios') return (await HubRuntime.getInfo()).running === true;
    return Boolean(await estadoCaja(LOCAL_HUB_URL, 1500));
  } catch { return false; }
}
